import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

function OTPVerificationStep({
    formData,
    nextStep,
    prevStep,
    otp,
    setOtp,
    handleVerifyOtp,
    handleResendOtp,
    otpVerified,
    otpLoading,
    phoneVerificationEnabled,
    configLoading,
}) {
    const [countdown, setCountdown] = useState(300);
    const [canResend, setCanResend] = useState(false);

    // Countdown timer
    useEffect(() => {
        if(countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [countdown]);

    // Format countdown as MM:SS
    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    const handleNext = async () => {
        console.log(
            "OTP handleNext called, phoneVerificationEnabled:",
            phoneVerificationEnabled,
            "configLoading:",
            configLoading,
            "otpVerified:",
            otpVerified,
        );

        if(!phoneVerificationEnabled || configLoading) {
            console.log("Skipping verification - disabled or loading");
            nextStep();
        } else if(otpVerified) {
            console.log("Already verified, proceeding to next step");
            nextStep();
        } else {
            console.log("Attempting to verify OTP");
            const isVerified = await handleVerifyOtp();
            console.log("Verification result:", isVerified);
            if(isVerified) {
                console.log("Verification successful, calling nextStep");
                nextStep();
            } else {
                console.log("Verification failed, staying on current step");
            }
        }
    };

    const handleResendCode = () => {
        setCountdown(300);
        setCanResend(false);
        handleResendOtp();
    };

    // If phone verification is disabled, automatically proceed
    useEffect(() => {
        if(!phoneVerificationEnabled && !configLoading) {
            nextStep();
        }
    }, [phoneVerificationEnabled, configLoading, nextStep]);

    // Show loading state if config is still loading
    if(configLoading) {
        return (
            <div className="w-full max-w-lg px-4 pt-8 pb-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto mb-4"></div>
                    <p className="text-gray-600">
                        Loading verification settings...
                    </p>
                </div>
            </div>
        );
    }

    // Skip this step if phone verification is disabled
    if(!phoneVerificationEnabled) {
        nextStep();
        return null;
    }

    return (
        <div className="w-full max-w-lg px-4 pt-8 pb-4">
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                    Enter Verification Code
                </h1>
                <p className="text-gray-600 mb-2">
                    Enter the one-time code we sent to {formData.phone}
                </p>
                <p className="text-sm text-gray-500">
                    This code is valid for {formatCountdown(countdown)} minutes.
                </p>
            </div>

            <div className="space-y-6">
                {/* OTP Input - 6 Digit Blocks */}
                <div className="flex justify-center gap-3">
                    {[0, 1, 2, 3, 4, 5].map((idx) => (
                        <input
                            key={idx}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={otp[idx] || ""}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if(value) {
                                    const newOtp = otp.split("");
                                    newOtp[idx] = value;
                                    setOtp(newOtp.slice(0, 6).join(""));
                                    // Move to next field
                                    if(idx < 5 && value) {
                                        const nextInput =
                                            document.querySelector(
                                                `input[data-otp-index="${
                                                    idx + 1
                                                }"]`,
                                            );
                                        if(nextInput) nextInput.focus();
                                    }
                                }
                            }}
                            onKeyDown={(e) => {
                                if(e.key === "Backspace") {
                                    e.preventDefault();
                                    if(otp[idx]) {
                                        // Delete character from current field
                                        const newOtp = otp.split("");
                                        newOtp[idx] = "";
                                        setOtp(newOtp.slice(0, 6).join(""));
                                    } else if(idx > 0) {
                                        // Move to previous field if current is empty
                                        const prevInput =
                                            document.querySelector(
                                                `input[data-otp-index="${
                                                    idx - 1
                                                }"]`,
                                            );
                                        if(prevInput) {
                                            const newOtp = otp.split("");
                                            newOtp[idx - 1] = "";
                                            setOtp(newOtp.slice(0, 6).join(""));
                                            prevInput.focus();
                                        }
                                    }
                                }
                            }}
                            data-otp-index={idx}
                            autoFocus={idx === 0}
                            className="w-12 h-14 text-center text-2xl font-semibold border-2 border-[#C2D7E2] rounded-lg focus:border-navy focus:ring-2 focus:ring-navy/20 transition outline-none bg-white hover:border-navy"
                        />
                    ))}
                </div>

                {/* Verification Status */}
                {otpVerified && (
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-[#C2D7E2] rounded-full mb-3">
                            <svg
                                className="w-6 h-6 text-navy"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <p className="text-navy font-medium">
                            Phone number verified!
                        </p>
                    </div>
                )}

                {/* Resend Code */}
                <div className="text-center">
                    {canResend ? (
                        <div className="space-y-2">
                            <button
                                onClick={handleResendCode}
                                className="text-navy hover:text-navy-2 font-medium underline"
                            >
                                Request the code
                            </button>
                            <p className="text-sm text-gray-500">
                                Having problems?{" "}
                                <span className="text-navy underline cursor-pointer">
                                    Contact support
                                </span>
                            </p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Resend code in {formatCountdown(countdown)} seconds
                        </p>
                    )}
                </div>

                {/* Verify Button */}
                <div className="text-center">
                    <button
                        onClick={handleNext}
                        disabled={
                            otpLoading || (!otpVerified && otp.length !== 6)
                        }
                        className={`w-full py-4 rounded-lg font-semibold text-lg transition-all ${
                            otpLoading
                                ? "bg-gray-400 text-white cursor-not-allowed"
                                : otpVerified || otp.length === 6
                                  ? "bg-navy text-white hover:bg-navy-2 shadow-lg"
                                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                        {otpLoading
                            ? "Verifying..."
                            : otpVerified
                              ? "Continue"
                              : "Verify"}
                    </button>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8">
                <button
                    onClick={prevStep}
                    className="px-8 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                >
                    Back
                </button>
            </div>
        </div>
    );
}

OTPVerificationStep.propTypes = {
    formData: PropTypes.object.isRequired,
    nextStep: PropTypes.func.isRequired,
    prevStep: PropTypes.func.isRequired,
    otp: PropTypes.string.isRequired,
    setOtp: PropTypes.func.isRequired,
    handleVerifyOtp: PropTypes.func.isRequired,
    handleResendOtp: PropTypes.func.isRequired,
    otpVerified: PropTypes.bool.isRequired,
    otpLoading: PropTypes.bool.isRequired,
    phoneVerificationEnabled: PropTypes.bool.isRequired,
    configLoading: PropTypes.bool.isRequired,
};

export default OTPVerificationStep;
